import React, { useState, useEffect, useCallback } from 'react';
import { Save, X, Users, Mail, Phone, Hash, Calendar, User, AlertCircle, CreditCard, Book, UserPlus, Camera, Key, Eye, EyeOff, Shield, CheckCircle, UserCheck, DollarSign } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import ImageUpload from '../ui/ImageUpload';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import CepAutoFill from './CepAutoFill';

const StudentForm = ({ onSubmit, onCancel, initialData = null, loading = false }) => {
  const { company } = useAuth();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [useExistingParent, setUseExistingParent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    // Dados básicos do aluno
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    registration_number: initialData?.registration_number || '',
    birth_date: initialData?.birth_date || '',
    class_name: initialData?.class_name || '',
    
    // Dados dos responsáveis - IDs das pessoas selecionadas
    selected_parent_id: initialData?.person_id || '',
    selected_financial_parent_id: initialData?.financial_parent_id || '',
    
    // Foto do aluno
    photo: null,
    photo_url: initialData?.photo_url || null,
    
    // Endereço
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    zip_code: initialData?.zip_code || '',
    
    // Acesso ao sistema
    create_system_access: initialData?.create_system_access || false,
    password: initialData ? '' : '',
    confirm_password: initialData ? '' : '',
    
    // Configurações financeiras
    balance: initialData?.balance || '0.00',
    credit_limit: initialData?.credit_limit || '0.00',
    payment_type: initialData?.payment_type || 'prepaid',
    
    // Configurações gerais
    active: initialData?.active !== undefined ? initialData.active : true,
    notes: initialData?.notes || ''
  });

  const [errors, setErrors] = useState({});
  // Carregar usuários disponíveis para seleção como responsáveis
  useEffect(() => {
    if (company?.id) {
      loadAvailableUsers();
    }
  }, [company]);

  // Atualizar formData quando initialData mudar (para edição)
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      // Buscar pessoas cadastradas da tabela people ao invés de users
      const people = await dataHelpers.getPeople(company.id);
      // Filtrar apenas pessoas ativas e do tipo "parent"
      const parentPeople = people.filter(person => 
        person.active && person.type === 'parent'
      );
      setAvailableUsers(parentPeople);
    } catch (error) {
      console.error('Error loading people:', error);
      toast.error('Erro ao carregar responsáveis cadastrados');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleParentSelectionChange = (selectedParentId) => {
    setFormData(prev => ({
      ...prev,
      selected_parent_id: selectedParentId
    }));
  };

  const handleFinancialParentSelectionChange = (selectedParentId) => {
    setFormData(prev => ({
      ...prev,
      selected_financial_parent_id: selectedParentId
    }));
  };

  const handleImageChange = (file, error) => {
    setFormData(prev => ({
      ...prev,
      photo: file
    }));
    
    if (error) {
      setErrors(prev => ({
        ...prev,
        photo: error
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        photo: null
      }));
    }
  };

  const handleCepAddress = (cepData) => {
    setFormData(prev => ({
      ...prev,
      address: cepData.street || prev.address,
      city: cepData.city || prev.city,
      state: cepData.state || prev.state,
      zip_code: cepData.cep || prev.zip_code
    }));
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value.slice(0, 15);
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const generatePassword = () => {
    const password = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-2).toUpperCase() + '123';
    setFormData(prev => ({
      ...prev,
      password,
      confirm_password: password
    }));
    toast.success('Senha gerada automaticamente');
  };

  const validateForm = () => {
    const newErrors = {};

    // Validações obrigatórias - Dados básicos
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.registration_number.trim()) {
      newErrors.registration_number = 'Matrícula é obrigatória';
    }

    if (!formData.class_name) {
      newErrors.class_name = 'Turma é obrigatória';
    }

    // Email é opcional, mas se fornecido deve ser válido
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validação de acesso ao sistema
    if (formData.create_system_access) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email é obrigatório para acesso ao sistema';
      }
      
      if (!initialData) { // Apenas para novo cadastro
        if (!formData.password) {
          newErrors.password = 'Senha é obrigatória para acesso ao sistema';
        } else if (formData.password.length < 6) {
          newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
        }

        if (formData.password && formData.password !== formData.confirm_password) {
          newErrors.confirm_password = 'Senhas não coincidem';
        }
      }
    }    // Validações dos responsáveis
    if (!formData.selected_parent_id) {
      newErrors.selected_parent_id = 'Selecione um responsável cadastrado';
    }

    // Validações do responsável financeiro - agora obrigatório
    if (!formData.selected_financial_parent_id) {
      newErrors.selected_financial_parent_id = 'Selecione um responsável financeiro cadastrado';
    }

    // Validações monetárias
    if (formData.balance && isNaN(parseFloat(formData.balance))) {
      newErrors.balance = 'Saldo deve ser um valor válido';
    }

    if (formData.credit_limit && isNaN(parseFloat(formData.credit_limit))) {
      newErrors.credit_limit = 'Limite de crédito deve ser um valor válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {      // Converter campos numéricos e limpar formatação
      const studentData = {
        ...formData,
        phone: formData.phone.replace(/\D/g, ''),
        zip_code: formData.zip_code.replace(/\D/g, ''),
        balance: parseFloat(formData.balance || 0),
        credit_limit: parseFloat(formData.credit_limit || 0),
        birth_date: formData.birth_date || null,
        // Mapear create_system_access para active (se tem acesso ao sistema, deve estar ativo)
        active: formData.active,
        // Se create_system_access está marcado, incluir password para criação de auth
        password: formData.create_system_access ? formData.password : undefined,
        // Mapear responsável selecionado para person_id
        person_id: formData.selected_parent_id || null,
        // Mapear responsável financeiro selecionado
        financial_parent_id: formData.selected_financial_parent_id || null
      };
      
      // Remove fields that should not be stored in students table
      delete studentData.create_system_access; // Campo apenas do formulário
      delete studentData.confirm_password;     // Campo apenas para validação
      delete studentData.selected_parent_id;   // Campo apenas para seleção
      delete studentData.selected_financial_parent_id; // Campo apenas para seleção

      await onSubmit(studentData);
    } catch (error) {
      console.error('Error submitting student:', error);
      toast.error('Erro ao salvar aluno');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {initialData ? 'Editar Aluno' : 'Cadastrar Novo Aluno'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {initialData ? 'Atualize as informações do aluno' : 'Preencha os dados do novo aluno'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Dados Básicos do Aluno */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              Dados Básicos do Aluno
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Informações pessoais e acadêmicas do estudante
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Nome Completo *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                placeholder="Nome completo do aluno"
                icon={User}
                required
              />
            </div>
            
            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="email@exemplo.com"
              icon={Mail}
            />

            <Input
              label="Telefone"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
              error={errors.phone}
              placeholder="(11) 99999-9999"
              icon={Phone}
            />

            <Input
              label="Número de Matrícula *"
              name="registration_number"
              value={formData.registration_number}
              onChange={handleChange}
              error={errors.registration_number}
              placeholder="Ex: 2024001"
              icon={Hash}
              required
            />

            <Input
              label="Data de Nascimento"
              name="birth_date"
              type="date"
              value={formData.birth_date}
              onChange={handleChange}
              error={errors.birth_date}
              icon={Calendar}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Turma *
              </label>
              <select
                name="class_name"
                value={formData.class_name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.class_name ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Selecione uma turma</option>
                <option value="1º Ano A">1º Ano A</option>
                <option value="1º Ano B">1º Ano B</option>
                <option value="2º Ano A">2º Ano A</option>
                <option value="2º Ano B">2º Ano B</option>
                <option value="3º Ano A">3º Ano A</option>
                <option value="3º Ano B">3º Ano B</option>
                <option value="4º Ano A">4º Ano A</option>
                <option value="4º Ano B">4º Ano B</option>
                <option value="5º Ano A">5º Ano A</option>
                <option value="5º Ano B">5º Ano B</option>
              </select>
              {errors.class_name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.class_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Pagamento
              </label>
              <select
                name="payment_type"
                value={formData.payment_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="prepaid">Pré-pago (Recarga)</option>
                <option value="credit">Crédito (Limite)</option>
                <option value="monthly">Mensalidade</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Foto do Aluno */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Camera className="w-5 h-5 mr-2 text-blue-600" />
              Foto do Aluno
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Adicione uma foto para identificação do aluno
            </p>
          </div>

          <ImageUpload
            label="Foto do Aluno"
            value={formData.photo_url}
            onChange={handleImageChange}
            error={errors.photo}
            placeholder="Clique para adicionar foto do aluno"
            accept="image/*"
            maxSize={3 * 1024 * 1024}
          />
        </Card>

        {/* Acesso ao Sistema */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <UserCheck className="w-5 h-5 mr-2 text-blue-600" />
              Acesso ao Sistema
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure se o aluno terá acesso ao sistema (portal do aluno)
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="create_system_access"
                checked={formData.create_system_access}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Dar acesso ao sistema para o aluno
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  O aluno poderá fazer login no sistema para ver saldo, compras, etc.
                </p>
              </div>
            </label>
          </div>

          {formData.create_system_access && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-blue-800 font-medium text-sm">
                    Email é obrigatório para acesso ao sistema
                  </span>
                </div>
              </div>

              {!initialData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <Input
                      label="Senha *"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      error={errors.password}
                      placeholder="Mínimo 6 caracteres"
                      icon={Key}
                      required={formData.create_system_access}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="relative">
                    <Input
                      label="Confirmar Senha *"
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={handleChange}
                      error={errors.confirm_password}
                      placeholder="Repita a senha"
                      icon={Key}
                      required={formData.create_system_access}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {!initialData && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={generatePassword}
                  className="text-sm"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Gerar Senha Automática
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Responsável Principal */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2 text-green-600" />
              Responsável Principal
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Selecione o responsável principal já cadastrado no sistema
            </p>
          </div>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <UserPlus className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-blue-800 font-medium">
                  Responsável Cadastrado *
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável Cadastrado *
                </label>
                <select
                  value={formData.selected_parent_id}
                  onChange={(e) => handleParentSelectionChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.selected_parent_id ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={loadingUsers}
                  required
                >
                  <option value="">
                    {loadingUsers ? 'Carregando responsáveis...' : 'Selecione um responsável cadastrado'}
                  </option>
                  {availableUsers.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name} - {person.email || person.phone || 'Sem contato'}
                    </option>
                  ))}
                </select>
                {errors.selected_parent_id && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.selected_parent_id}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>        {/* Responsável Financeiro */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-purple-600" />
              Responsável Financeiro
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Selecione o responsável financeiro já cadastrado (obrigatório)
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CreditCard className="w-5 h-5 text-purple-600 mr-2" />
                <span className="text-purple-800 font-medium">
                  Responsável Financeiro Cadastrado *
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável Financeiro Cadastrado *
                </label>
                <select
                  value={formData.selected_financial_parent_id}
                  onChange={(e) => handleFinancialParentSelectionChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                    errors.selected_financial_parent_id ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={loadingUsers}
                  required
                >
                  <option value="">
                    {loadingUsers ? 'Carregando responsáveis...' : 'Selecione um responsável financeiro cadastrado'}
                  </option>
                  {availableUsers.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name} - {person.email || person.phone || 'Sem contato'}
                    </option>
                  ))}
                </select>
                {errors.selected_financial_parent_id && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.selected_financial_parent_id}
                  </p>
                )}                <p className="mt-1 text-xs text-gray-500">
                  O responsável financeiro pode ser o mesmo responsável principal ou uma pessoa diferente
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Endereço */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-orange-600" />
              Endereço
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Endereço residencial do aluno
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Endereço"
                name="address"
                value={formData.address}
                onChange={handleChange}
                error={errors.address}
                placeholder="Rua, número, complemento"
              />
            </div>
            <CepAutoFill
              label="CEP"
              value={formData.zip_code}
              onAddress={handleCepAddress}
              onChange={e => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
              error={errors.zip_code}
              placeholder="00000-000"
              name="zip_code"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <Input
              label="Cidade"
              name="city"
              value={formData.city}
              onChange={handleChange}
              error={errors.city}
              placeholder="Nome da cidade"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione um estado</option>
                <option value="AC">AC</option>
                <option value="AL">AL</option>
                <option value="AP">AP</option>
                <option value="AM">AM</option>
                <option value="BA">BA</option>
                <option value="CE">CE</option>
                <option value="DF">DF</option>
                <option value="ES">ES</option>
                <option value="GO">GO</option>
                <option value="MA">MA</option>
                <option value="MT">MT</option>
                <option value="MS">MS</option>
                <option value="MG">MG</option>
                <option value="PA">PA</option>
                <option value="PB">PB</option>
                <option value="PR">PR</option>
                <option value="PE">PE</option>
                <option value="PI">PI</option>
                <option value="RJ">RJ</option>
                <option value="RN">RN</option>
                <option value="RS">RS</option>
                <option value="RO">RO</option>
                <option value="RR">RR</option>
                <option value="SC">SC</option>
                <option value="SP">SP</option>
                <option value="SE">SE</option>
                <option value="TO">TO</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Configurações Financeiras */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              Configurações Financeiras
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Saldo inicial e limites do aluno
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Saldo Inicial"
              name="balance"
              type="number"
              step="0.01"
              min="0"
              value={formData.balance}
              onChange={handleChange}
              error={errors.balance}
              placeholder="0.00"
              icon={DollarSign}
            />

            <Input
              label="Limite de Crédito"
              name="credit_limit"
              type="number"
              step="0.01"
              min="0"
              value={formData.credit_limit}
              onChange={handleChange}
              error={errors.credit_limit}
              placeholder="0.00"
              icon={CreditCard}
            />
          </div>
        </Card>

        {/* Configurações Gerais */}
        <Card className="p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-gray-600" />
              Configurações Gerais
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Status e observações do aluno
            </p>
          </div>

          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Aluno ativo
              </span>
            </label>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Observações sobre o aluno..."
              />
            </div>
          </div>
        </Card>

        {/* Botões de Ação */}
        <Card className="p-6">
          <div className="flex items-center justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : initialData ? 'Atualizar Aluno' : 'Cadastrar Aluno'}
            </Button>
          </div>
        </Card>

        {/* Alertas de Erro */}
        {Object.keys(errors).length > 0 && (
          <Card className="p-6 bg-red-50 border-red-200">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <h4 className="font-medium text-red-800">
                  Corrija os seguintes erros:
                </h4>
                <ul className="text-sm text-red-700 mt-1">
                  {Object.values(errors).map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        )}
      </form>
    </div>
  );
};

export default StudentForm;